/**
 * useTourConfig
 *
 * Loads tour configuration for a module, filtered by role.
 * Falls back to the client-safe registry populated at server startup.
 */

'use client';

import { useEffect, useMemo, useState } from 'react';
import type {
  TourFlow,
  TourModuleConfig,
  TourObjective,
  TourGlobalConfig,
} from '@/config/guided-tours/types';
import { flowMatchesRole } from '@/config/guided-tours/roleFilter';

interface UseTourConfigOptions {
  moduleKey: string;
  role?: string;
  enabled?: boolean;
}

// Session-cached list of module keys that have a YAML tour config. Providers
// mount on many surfaces whose context has no tour (studio designers, root /
// support-desk / accounts directory views, stray guided-tour-open events) —
// checking the registry list first keeps those mounts from spamming 404s on
// /guided-tours/modules/{key}. A null set means the list fetch failed, in
// which case callers fall through to the direct fetch (previous behaviour).
let knownModulesPromise: Promise<Set<string> | null> | null = null;

function getKnownTourModules(): Promise<Set<string> | null> {
  if (!knownModulesPromise) {
    knownModulesPromise = fetch('/api/v1/guided-tours/modules')
      .then((res) => (res.ok ? res.json() : null))
      .then((data) => {
        if (!data?.success || !Array.isArray(data.modules)) return null;
        return new Set<string>(
          data.modules
            .map((m: { module_key?: unknown }) => m?.module_key)
            .filter((k: unknown): k is string => typeof k === 'string' && k.length > 0),
        );
      })
      .catch(() => null);
  }
  return knownModulesPromise;
}

export interface UseTourConfigResult {
  moduleConfig: TourModuleConfig | null;
  schema: TourGlobalConfig | null;
  flows: TourFlow[];
  objectives: TourObjective[];
  masterPrompt: string | null;
  loading: boolean;
  error: string | null;
  refetch: () => void;
}

export function useTourConfig({
  moduleKey,
  role,
  enabled = true,
}: UseTourConfigOptions): UseTourConfigResult {
  const [moduleConfig, setModuleConfig] = useState<TourModuleConfig | null>(null);
  const [schema, setSchema] = useState<TourGlobalConfig | null>(null);
  const [masterPrompt, setMasterPrompt] = useState<string | null>(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [refreshToken, setRefreshToken] = useState(0);

  const refetch = () => setRefreshToken((token) => token + 1);

  useEffect(() => {
    if (!enabled || !moduleKey) {
      setModuleConfig(null);
      setSchema(null);
      return;
    }

    let cancelled = false;

    const load = async () => {
      setLoading(true);
      setError(null);

      try {
        const known = await getKnownTourModules();
        if (known && !known.has(moduleKey)) {
          if (!cancelled) {
            setModuleConfig(null);
            setSchema(null);
            setMasterPrompt(null);
          }
          return;
        }

        const roleParam = role ? `?role=${encodeURIComponent(role)}` : '';
        const response = await fetch(`/api/v1/guided-tours/modules/${moduleKey}${roleParam}`);
        if (!response.ok) {
          throw new Error(`Failed to load tour config: ${response.statusText}`);
        }

        const data = await response.json();
        if (!data?.success) {
          throw new Error(data?.error || 'Tour config returned unsuccessfully');
        }

        if (!cancelled) {
          setModuleConfig(data.module ?? null);
          setSchema(data.schema ?? null);
          setMasterPrompt(data.master_prompt ?? null);
        }
      } catch (err) {
        if (!cancelled) {
          setError(err instanceof Error ? err.message : String(err));
        }
      } finally {
        if (!cancelled) {
          setLoading(false);
        }
      }
    };

    load();

    return () => {
      cancelled = true;
    };
  }, [moduleKey, role, enabled, refreshToken]);

  const flows = useMemo(() => {
    if (!moduleConfig?.flows) {
      return [];
    }

    return moduleConfig.flows.filter((flow) => flowMatchesRole(flow, role));
  }, [moduleConfig, role]);

  const objectives = useMemo(() => {
    return moduleConfig?.objectives ?? [];
  }, [moduleConfig]);

  return {
    moduleConfig,
    schema,
    flows,
    objectives,
    masterPrompt,
    loading,
    error,
    refetch,
  };
}
